import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';

interface ProjectKPIsProps {
    kpis: { label: string; value: string }[];
}

function Counter({ to, duration = 2 }: { to: string; duration?: number }) {
    const nodeRef = useRef<HTMLSpanElement>(null);
    const inView = useInView(nodeRef, { once: true, margin: "-100px" });

    // Extract number and suffix/prefix if possible
    const numValue = parseFloat(to.replace(/[^0-9.-]+/g, ""));
    const isNumber = !isNaN(numValue);
    const prefix = to.match(/^[^0-9]*/) ? to.match(/^[^0-9]*/)![0] : "";
    const suffix = to.match(/[^0-9]*$/) ? to.match(/[^0-9]*$/)![0] : "";

    useEffect(() => {
        if (!inView || !isNumber || !nodeRef.current) return;

        let startTime: number;
        let animationFrame: number;

        const animate = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

            // Easing function (easeOutExpo)
            const easeValue = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);

            const current = Math.floor(numValue * easeValue);
            nodeRef.current!.textContent = `${prefix}${current}${suffix}`;

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            } else {
                nodeRef.current!.textContent = to; // Ensure final value matches exactly
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrame);
    }, [inView, numValue, duration, to, prefix, suffix, isNumber]);

    return <span ref={nodeRef}>{isNumber ? prefix + "0" + suffix : to}</span>;
}

export function ProjectKPIs({ kpis }: ProjectKPIsProps) {
    if (!kpis || kpis.length === 0) return null;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center w-full max-w-6xl mx-auto">
            {kpis.map((kpi, index) => (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="w-full max-w-xs"
                >
                    <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-indigo-600/20 to-blue-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" />
                        <div className="relative h-full bg-gradient-to-br from-blue-900/30 via-indigo-900/20 to-blue-800/30 backdrop-blur-sm border border-blue-500/20 rounded-2xl p-6 text-center hover:border-blue-400/40 transition-all duration-300 hover:-translate-y-1">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="relative z-10">
                                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-blue-400 via-blue-300 to-indigo-400 bg-clip-text text-transparent mb-3">
                                    <Counter to={kpi.value} />
                                </div>
                                <div className="text-sm font-medium text-blue-200/80 uppercase tracking-wider">
                                    {kpi.label}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
}
